/**
 * ClientDetail — 360° client view.
 *
 * Single page that fuses Finance + CRM + Pipeline:
 *  • Header: company / contact / tax-id / industry / country
 *  • KPI strip: Lifetime Revenue · Paid · Outstanding · Open Invoices · Open Opps · Pipeline Value
 *  • Tabs: Invoices · Opportunities · Activity
 *
 * Backed by GET /api/crm/clients/{id}/360 — one network call.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Mail, Phone, Globe, MapPin, Hash, Building2,
  Wallet, FileText, TrendingUp, Activity as ActivityIcon, Loader2, Plus,
  Download, Send,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtCurrency = (n, c = 'AED') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: c, maximumFractionDigits: 2 }).format(Number(n) || 0);

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-GB'); } catch { return s; }
};

const STATUS_COLOR = {
  draft: 'bg-gray-100 text-gray-700',
  pro_forma: 'bg-amber-50 text-amber-800 border border-amber-200',
  proforma: 'bg-amber-50 text-amber-800 border border-amber-200',
  issued: 'bg-blue-50 text-blue-800 border border-blue-200',
  sent: 'bg-blue-50 text-blue-800 border border-blue-200',
  partial: 'bg-purple-50 text-purple-800 border border-purple-200',
  paid: 'bg-green-50 text-green-700 border border-green-200',
  overdue: 'bg-red-50 text-red-700 border border-red-200',
  cancelled: 'bg-gray-100 text-gray-500 line-through',
  void: 'bg-gray-100 text-gray-500 line-through',
};

const KPI = ({ label, value, tone, sub, testId }) => (
  <div
    className={`rounded-xl border px-4 py-3 ${tone || 'bg-white'}`}
    data-testid={testId}
  >
    <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
    <div className="text-lg font-bold text-[#2B2D42]">{value}</div>
    {sub && <div className="text-[11px] text-gray-500">{sub}</div>}
  </div>
);

const TabButton = ({ active, onClick, icon: Icon, label, count, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 -mb-px border-b-2 font-semibold text-sm flex items-center gap-2 transition ${
      active
        ? 'border-[#00B4D8] text-[#023E8A]'
        : 'border-transparent text-gray-500 hover:text-[#023E8A]'
    }`}
    data-testid={testId}
  >
    <Icon className="w-4 h-4" />
    {label}
    {count != null && (
      <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${active ? 'bg-[#00B4D8] text-white' : 'bg-gray-100 text-gray-600'}`}>
        {count}
      </span>
    )}
  </button>
);

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('invoices');
  const [sendingStatement, setSendingStatement] = useState(false);

  const downloadStatement = () => {
    const url = `${API_URL}/api/crm/clients/${id}/statement.pdf`;
    // Use fetch+blob to honour Bearer auth, then trigger native download.
    axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    }).then((r) => {
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Statement-${(client?.company_name || 'client').replace(/[^a-z0-9-]+/gi, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    }).catch((e) => {
      toast({
        variant: 'destructive',
        title: 'Could not download statement',
        description: e.response?.data?.detail || 'Try again or contact support.',
      });
    });
  };

  const emailStatement = async () => {
    if (!data?.client?.email) {
      toast({ variant: 'destructive', title: 'No email on file', description: 'Add an email address before sending.' });
      return;
    }
    if (!window.confirm(
      `Email the Statement of Account to ${data.client.email}?\n\n` +
      `It will include every issued / partial / paid / overdue invoice for this client.`,
    )) return;
    setSendingStatement(true);
    try {
      const r = await axios.post(`${API_URL}/api/crm/clients/${id}/statement/send`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({
        title: 'Statement sent',
        description: `Emailed to ${r.data.to}. Outstanding ${r.data.currency} ${Number(r.data.outstanding).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`,
      });
      // Refresh activity tab so the new entry appears.
      const refreshed = await axios.get(`${API_URL}/api/crm/clients/${id}/360`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(refreshed.data);
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Could not send statement',
        description: e.response?.data?.detail || 'SendGrid rejected the message.',
      });
    } finally {
      setSendingStatement(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/crm/clients/${id}/360`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setData(r.data);
      } catch (e) {
        toast({
          variant: 'destructive',
          title: 'Failed to load 360° view',
          description: e.response?.data?.detail || 'Could not fetch client details',
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, token, toast]);

  const client = data?.client;
  const kpis = data?.kpis;
  const currency = useMemo(() => {
    const inv = data?.invoices?.[0];
    return inv?.currency || 'AED';
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-[#00B4D8]" />
      </div>
    );
  }

  if (!client) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-gray-500">Client not found.</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="client-360-page">
      <button
        type="button"
        onClick={() => navigate('/admin/crm/clients')}
        className="inline-flex items-center gap-1 text-sm text-[#023E8A] hover:underline"
        data-testid="client-360-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Clients
      </button>

      {/* Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#023E8A] to-[#00B4D8] text-white flex items-center justify-center font-bold text-xl shrink-0">
              {(client.company_name || '?').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-2xl font-bold text-[#2B2D42]" data-testid="client-name">
                  {client.company_name}
                </h2>
                {client.industry && (
                  <Badge variant="outline" className="capitalize">{client.industry}</Badge>
                )}
                {client.country && (
                  <Badge variant="outline" className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {client.country}
                  </Badge>
                )}
                {(client.tags || []).map((t) => (
                  <Badge key={t} className="bg-[#E0F2FE] text-[#00B4D8]">{t}</Badge>
                ))}
              </div>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600">
                {client.contact_person && (
                  <div className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {client.contact_person}</div>
                )}
                {client.email && (
                  <a href={`mailto:${client.email}`} className="flex items-center gap-1 hover:text-[#00B4D8] truncate">
                    <Mail className="w-3.5 h-3.5" /> {client.email}
                  </a>
                )}
                {client.phone && (
                  <a href={`tel:${client.phone}`} className="flex items-center gap-1 hover:text-[#00B4D8]">
                    <Phone className="w-3.5 h-3.5" /> {client.phone}
                  </a>
                )}
                {client.website && (
                  <a href={client.website.startsWith('http') ? client.website : `https://${client.website}`}
                     target="_blank" rel="noreferrer"
                     className="flex items-center gap-1 hover:text-[#00B4D8] truncate">
                    <Globe className="w-3.5 h-3.5" /> {client.website}
                  </a>
                )}
                {client.tax_id && (
                  <div className="flex items-center gap-1">
                    <Hash className="w-3.5 h-3.5" /> <span className="font-mono">{client.tax_id}</span>
                  </div>
                )}
              </div>
              {client.notes && (
                <p className="mt-2 text-sm text-gray-500 italic">{client.notes}</p>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={downloadStatement}
                className="text-[#023E8A] border-[#023E8A]/30 hover:bg-[#023E8A]/5"
                data-testid="download-statement-btn"
              >
                <Download className="w-4 h-4 mr-1" /> Download Statement
              </Button>
              <Button
                size="sm"
                onClick={emailStatement}
                disabled={sendingStatement || !client.email}
                className="bg-[#00B4D8] hover:bg-[#0096c7]"
                data-testid="email-statement-btn"
                title={!client.email ? 'Client has no email on file' : 'Email Statement of Account via SendGrid'}
              >
                {sendingStatement
                  ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  : <Send className="w-4 h-4 mr-1" />}
                Email Statement
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="client-360-kpis">
          <KPI label="Lifetime Revenue" value={fmtCurrency(kpis.lifetime_revenue, currency)} tone="bg-blue-50 border-blue-100" testId="kpi-lifetime-revenue" />
          <KPI label="Paid" value={fmtCurrency(kpis.total_paid, currency)} tone="bg-green-50 border-green-100" sub={`${kpis.invoices_paid} invoices`} testId="kpi-paid" />
          <KPI label="Outstanding" value={fmtCurrency(kpis.outstanding_balance, currency)} tone="bg-amber-50 border-amber-100" sub={`${kpis.invoices_open} open`} testId="kpi-outstanding" />
          <KPI label="Open Invoices" value={kpis.invoices_open} sub={`of ${kpis.invoices_total} total`} testId="kpi-invoices-open" />
          <KPI label="Open Opportunities" value={kpis.open_opportunities} testId="kpi-opps-open" />
          <KPI label="Pipeline Value" value={fmtCurrency(kpis.pipeline_value, currency)} tone="bg-purple-50 border-purple-100" sub={`${fmtCurrency(kpis.won_value, currency)} won`} testId="kpi-pipeline" />
        </div>
      )}

      {/* Tabs */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 border-b mb-3">
            <TabButton
              active={tab === 'invoices'}
              onClick={() => setTab('invoices')}
              icon={FileText}
              label="Invoices"
              count={data?.invoices?.length || 0}
              testId="tab-invoices"
            />
            <TabButton
              active={tab === 'opportunities'}
              onClick={() => setTab('opportunities')}
              icon={TrendingUp}
              label="Opportunities"
              count={data?.opportunities?.length || 0}
              testId="tab-opportunities"
            />
            <TabButton
              active={tab === 'activity'}
              onClick={() => setTab('activity')}
              icon={ActivityIcon}
              label="Activity"
              count={data?.activities?.length || 0}
              testId="tab-activity"
            />
            <div className="ml-auto">
              {tab === 'invoices' && (
                <Link to={`/admin/finance/invoices/create?client_id=${client.id}`}>
                  <Button size="sm" className="bg-[#00B4D8] hover:bg-[#0096c7]" data-testid="new-invoice-btn">
                    <Plus className="w-4 h-4 mr-1" /> New Invoice
                  </Button>
                </Link>
              )}
              {tab === 'opportunities' && (
                <Link to={`/admin/crm/opportunities?client_id=${client.id}`}>
                  <Button size="sm" className="bg-[#00B4D8] hover:bg-[#0096c7]" data-testid="new-opp-btn">
                    <Plus className="w-4 h-4 mr-1" /> New Opportunity
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Invoices tab */}
          {tab === 'invoices' && (
            <div data-testid="invoices-panel">
              {(data?.invoices?.length || 0) === 0 ? (
                <div className="text-sm text-gray-500 py-8 text-center bg-gray-50 rounded-lg border border-dashed">
                  No invoices yet for this client.
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Number</th>
                        <th className="px-3 py-2 text-left">Issue</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-right">Total</th>
                        <th className="px-3 py-2 text-right">Paid</th>
                        <th className="px-3 py-2 text-right">Outstanding</th>
                        <th className="px-3 py-2" />
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {data.invoices.map((inv) => {
                        const status = (inv.status || '').toLowerCase();
                        const total = Number(inv.total) || 0;
                        const paid = Number(inv.amount_paid) || 0;
                        const bal = inv.balance_due != null ? Number(inv.balance_due) : Math.max(0, total - paid);
                        return (
                          <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                            <td className="px-3 py-2 font-mono text-xs">
                              <Link to={`/admin/finance/invoices/${inv.id}`} className="text-[#023E8A] hover:underline">
                                {inv.invoice_number || inv.id.slice(0, 8)}
                              </Link>
                            </td>
                            <td className="px-3 py-2">{fmtDate(inv.issue_date)}</td>
                            <td className="px-3 py-2">
                              <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${STATUS_COLOR[status] || 'bg-gray-100 text-gray-600'}`}>
                                {status || '—'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right font-semibold">{fmtCurrency(total, inv.currency)}</td>
                            <td className="px-3 py-2 text-right text-green-700">{fmtCurrency(paid, inv.currency)}</td>
                            <td className={`px-3 py-2 text-right font-semibold ${bal > 0.01 ? 'text-amber-700' : 'text-gray-400'}`}>
                              {fmtCurrency(bal, inv.currency)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <Link to={`/admin/finance/invoices/${inv.id}`} className="text-xs text-[#00B4D8] hover:underline">View</Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Opportunities tab */}
          {tab === 'opportunities' && (
            <div data-testid="opportunities-panel">
              {(data?.opportunities?.length || 0) === 0 ? (
                <div className="text-sm text-gray-500 py-8 text-center bg-gray-50 rounded-lg border border-dashed">
                  No opportunities for this client.
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Stage</th>
                        <th className="px-3 py-2 text-right">Value</th>
                        <th className="px-3 py-2 text-right">Probability</th>
                        <th className="px-3 py-2 text-left">Expected close</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {data.opportunities.map((o) => (
                        <tr key={o.id} data-testid={`opp-row-${o.id}`}>
                          <td className="px-3 py-2">
                            <Link to={`/admin/crm/opportunities/${o.id}`} className="text-[#023E8A] hover:underline">
                              {o.name}
                            </Link>
                          </td>
                          <td className="px-3 py-2 capitalize">{(o.stage || '').replace('_', ' ')}</td>
                          <td className="px-3 py-2 text-right font-semibold">{fmtCurrency(o.value, o.currency)}</td>
                          <td className="px-3 py-2 text-right">{o.probability != null ? `${o.probability}%` : '—'}</td>
                          <td className="px-3 py-2">{fmtDate(o.expected_close_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Activity tab */}
          {tab === 'activity' && (
            <div data-testid="activity-panel">
              {(data?.activities?.length || 0) === 0 ? (
                <div className="text-sm text-gray-500 py-8 text-center bg-gray-50 rounded-lg border border-dashed">
                  No activity recorded yet.
                </div>
              ) : (
                <ul className="space-y-2">
                  {data.activities.map((a) => (
                    <li key={a.id} className="flex items-start gap-3 border-l-2 border-[#00B4D8] pl-3 py-1">
                      <div className="flex-1">
                        <div className="text-sm font-medium capitalize">
                          {(a.activity_type || a.type || 'note').replace('_', ' ')}{' '}
                          {a.title && <span className="text-gray-700">— {a.title}</span>}
                        </div>
                        {a.description && (
                          <div className="text-xs text-gray-500 mt-0.5">{a.description}</div>
                        )}
                        <div className="text-[10px] text-gray-400 mt-1">{fmtDate(a.created_at)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientDetail;
